// The playlist model, and the pure operations over it.
//
// Playlists moved OUT of the store and onto the document metadata contract
// (`DocumentMeta`) because they were the last user-owned entity that carried
// no identity beyond a name: no revision, no timestamps, no controller, no
// record that a deletion ever happened. Scores, Arrangements, Grooves and
// Media already carry all of that, which is what lets #71's AccountStore be a
// SWAP for the browser store rather than a rewrite. A playlist that stays a
// bare `{id, name, entries}` array would have to be migrated against real
// users' localStorage at exactly the moment sync ships — the worst time.
//
// `PlaylistDoc` is deliberately NOT a member of `StoredDocument`. That union
// drives repository path resolution, deterministic serialization and the
// rights gate, and a playlist is not repository content — nobody reviews a
// setlist for redistribution. It shares the metadata CONTRACT (identity,
// revision, controller) without claiming to be a catalogue document.

import type { DocumentMeta } from './documentSchema';
import { shortHash } from './documentSchema';
import type { EndingOptions } from '@/audio/endings';
import type { ArrangementPolicy } from '@/music/performance';
import type { StyleBand } from '@/music/style';

/** Bumped when the playlist shape changes, independently of `SCHEMA_VERSION`. */
export const PLAYLIST_SCHEMA_VERSION = 1;

/** A tune saved into a custom playlist — the verbatim iReal chunk, re-parsed on open. */
export interface SavedTune {
  title: string;
  composer: string;
  scheme: string;
  chunk: string;
}

/** Stable identity for an imported tune; titles and composers are not unique. */
export function savedTuneKey(tune: Pick<SavedTune, 'scheme' | 'chunk'>): string {
  return `${tune.scheme}\u0000${tune.chunk}`;
}

/** What a playlist row POINTS AT. Historically a raw iReal chunk (SavedTune);
 * a typed reference since v6 so catalogue, openbook and saved Scores can join
 * playlists too. */
export type PlaylistTuneRef =
  /** A tune the row CARRIES: an iReal song chunk (scheme irealb/irealbook) or,
   * since Oct 2026, one ABC tune (scheme 'abc', src/music/tuneIndex.ts). The
   * kind keeps its first name, so a build that predates ABC keeps the row
   * (it validates any scheme) rather than dropping it from a shared playlist. */
  | ({ kind: 'ireal' } & SavedTune)
  | {
      kind: 'score';
      id: string;
      revisionId: string;
      title: string;
      composer?: string;
    }
  | { kind: 'openbook'; id: string; file: string; title: string; composer?: string }
  | { kind: 'realbook'; id: string; file: string; title: string; composer?: string }
  | { kind: 'saved'; id: string; title: string; composer?: string }
  /** A tune an admin promoted to the library (`library_tunes`, migration 0009),
   * readable by everyone — so unlike 'saved', it resolves on every device. */
  | { kind: 'library'; id: string; title: string; composer?: string }
  /** A tune its owner shared from their account (`user_tunes`, migration 0010):
   * how a published setlist carries the author's own tunes, readable by any
   * signed-in member. `id` is the tune's own id. */
  | { kind: 'account'; id: string; owner: string; title: string; composer?: string };

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

/** Validate a ref read from untrusted data (a fetched setlist, persisted
 * history) against the PlaylistTuneRef union; null drops it. */
export function parsePlaylistTuneRef(value: unknown): PlaylistTuneRef | null {
  if (!value || typeof value !== 'object') return null;
  const ref = value as Record<string, unknown>;
  const title = str(ref.title);
  const composer = typeof ref.composer === 'string' ? ref.composer : undefined;
  switch (ref.kind) {
    case 'ireal': {
      const scheme = str(ref.scheme);
      const chunk = str(ref.chunk);
      if (!title || !scheme || !chunk) return null;
      return { kind: 'ireal', title, composer: composer ?? '', scheme, chunk };
    }
    case 'openbook':
    case 'realbook': {
      const id = str(ref.id);
      const file = str(ref.file);
      if (!id || !file || !title) return null;
      return { kind: ref.kind, id, file, title, ...(composer ? { composer } : {}) };
    }
    case 'score': {
      const id = str(ref.id);
      const revisionId = str(ref.revisionId);
      if (!id || !revisionId || !title) return null;
      return { kind: 'score', id, revisionId, title, ...(composer ? { composer } : {}) };
    }
    case 'saved':
    case 'library': {
      const id = str(ref.id);
      if (!id || !title) return null;
      return { kind: ref.kind, id, title, ...(composer ? { composer } : {}) };
    }
    case 'account': {
      const id = str(ref.id);
      const owner = str(ref.owner);
      if (!id || !owner || !title) return null;
      return { kind: 'account', id, owner, title, ...(composer ? { composer } : {}) };
    }
    default:
      return null;
  }
}

/** Identity of the TUNE a row points at — used to dedupe suggestions and to
 * match a row against the browser. Note this is the identity of the referent,
 * not of the row: two rows in one setlist can share it. */
export function playlistRefKey(ref: PlaylistTuneRef): string {
  return ref.kind === 'ireal' ? `ireal:${savedTuneKey(ref)}` : `${ref.kind}:${ref.id}`;
}

/**
 * One row in a setlist.
 *
 * `entryId` is the row's own identity and is NOT derived from the ref, which
 * buys two things a derived key cannot. A set can open with a tune and reprise
 * it as the encore — real setlists do this, and keying by referent made it
 * unrepresentable. And when two devices reorder the same list, a merge can
 * only match rows that have their own stable names.
 */
export interface PlaylistEntry {
  entryId: string;
  ref: PlaylistTuneRef;
  /** A performance note: "cue the head out", "trade 8s", "capo 2". */
  note?: string;
  /** How the band plays this tune in this playlist (Save to playlist). */
  call?: PlaylistCall;
}

/**
 * The CALL: how a playlist plays one of its tunes — the key, tempo, feel,
 * chorus count, form and ending a bandleader calls on the stand (user ruling
 * Sep 2026). Saved on purpose by anyone who can edit the playlist, and applied
 * when the tune is opened FROM that row, over the player's own settings for
 * the fields it sets. The music itself never lives here (changing it is Save
 * a copy), and neither does anything personal: the mix, the sounds, the comp
 * instrument, the voicings, the view. Every field is optional because a row
 * written by another client may carry only some of them.
 */
export interface PlaylistCall {
  /** A tonic as the Key chip names it ("Bb"); the mode is the tune's own. */
  key?: string;
  tempo?: number;
  /** The feel: the band's groove fields only (`FEEL_KEYS`), never its mutes or instrument. */
  band?: Partial<StyleBand>;
  repeats?: number;
  policy?: ArrangementPolicy;
  ending?: EndingOptions;
}

/** A user-curated, named, ordered set of tunes. */
export interface PlaylistDoc extends DocumentMeta {
  kind: 'playlist';
  name: string;
  description?: string;
  entries: PlaylistEntry[];
  /** ISO 8601. */
  createdAt: string;
  updatedAt: string;
}

/**
 * A playlist that was deleted.
 *
 * Kept because absence and deletion are different facts. Without a tombstone,
 * a sync that finds a list on the server and not on the client cannot tell
 * "this device deleted it" from "this device has not seen it yet", and the
 * safe-looking default — re-download it — resurrects exactly the list the user
 * just threw away.
 */
export interface PlaylistTombstone {
  id: string;
  deletedAt: string;
}

/** How long a tombstone is worth keeping. Long enough that a device offline
 * for a season still learns about the deletion; short enough that the list
 * does not grow forever. */
const TOMBSTONE_TTL_MS = 180 * 24 * 60 * 60 * 1000;

// --- identity ---------------------------------------------------------------

function randomSeed(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** `<slug>-<hash>`, matching `makeDocumentId`'s shape so playlist ids satisfy
 * `isValidDocumentId` and read the same way in a debugger. Assigned once; a
 * rename never moves a playlist's data. */
export function makePlaylistId(name: string, seed: string = randomSeed()): string {
  const slug =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'playlist';
  return `${slug}-${shortHash(seed)}`;
}

export function makeEntryId(seed: string = randomSeed()): string {
  return `e-${shortHash(seed)}${shortHash(`${seed}:2`)}`;
}

// --- construction and mutation ----------------------------------------------

const nowIso = () => new Date().toISOString();

export function createPlaylistDoc(name: string, now: string = nowIso()): PlaylistDoc {
  const trimmed = name.trim() || 'Untitled';
  return {
    kind: 'playlist',
    id: makePlaylistId(trimmed),
    schemaVersion: PLAYLIST_SCHEMA_VERSION,
    name: trimmed,
    entries: [],
    createdAt: now,
    updatedAt: now,
  };
}

/** Every mutation goes through here, so `updatedAt` cannot be forgotten at one
 * call site and silently break last-writer-wins ordering later. */
function touch(doc: PlaylistDoc, patch: Partial<PlaylistDoc>, now: string): PlaylistDoc {
  return { ...doc, ...patch, updatedAt: now };
}

export function renamePlaylistDoc(
  doc: PlaylistDoc,
  name: string,
  now: string = nowIso(),
): PlaylistDoc {
  const trimmed = name.trim();
  if (!trimmed || trimmed === doc.name) return doc;
  return touch(doc, { name: trimmed }, now);
}

export function setPlaylistDescription(
  doc: PlaylistDoc,
  description: string,
  now: string = nowIso(),
): PlaylistDoc {
  const trimmed = description.trim();
  if (trimmed === (doc.description ?? '')) return doc;
  return touch(doc, { description: trimmed || undefined }, now);
}

export function addEntry(
  doc: PlaylistDoc,
  ref: PlaylistTuneRef,
  options: { note?: string; entryId?: string; now?: string } = {},
): PlaylistDoc {
  const entry: PlaylistEntry = {
    entryId: options.entryId ?? makeEntryId(),
    ref,
    ...(options.note?.trim() ? { note: options.note.trim() } : {}),
  };
  return touch(doc, { entries: [...doc.entries, entry] }, options.now ?? nowIso());
}

export function removeEntry(
  doc: PlaylistDoc,
  entryId: string,
  now: string = nowIso(),
): PlaylistDoc {
  const entries = doc.entries.filter((e) => e.entryId !== entryId);
  if (entries.length === doc.entries.length) return doc;
  return touch(doc, { entries }, now);
}

/**
 * Move a row to an absolute index in the list.
 *
 * The target is clamped rather than rejected because both callers — a drop at
 * the end of a list and an Alt+Down on the last row — naturally ask for an
 * index one past the end, and a "move" that silently does nothing reads as a
 * broken drag.
 */
export function moveEntry(
  doc: PlaylistDoc,
  entryId: string,
  toIndex: number,
  now: string = nowIso(),
): PlaylistDoc {
  const from = doc.entries.findIndex((e) => e.entryId === entryId);
  if (from < 0) return doc;
  const to = Math.max(0, Math.min(doc.entries.length - 1, Math.trunc(toIndex)));
  if (to === from) return doc;
  const entries = [...doc.entries];
  const [moved] = entries.splice(from, 1);
  entries.splice(to, 0, moved);
  return touch(doc, { entries }, now);
}

export function setEntryNote(
  doc: PlaylistDoc,
  entryId: string,
  note: string,
  now: string = nowIso(),
): PlaylistDoc {
  const trimmed = note.trim();
  let changed = false;
  const entries = doc.entries.map((e) => {
    if (e.entryId !== entryId || (e.note ?? '') === trimmed) return e;
    changed = true;
    const { note: _dropped, ...rest } = e;
    return trimmed ? { ...rest, note: trimmed } : rest;
  });
  return changed ? touch(doc, { entries }, now) : doc;
}

/** Change only the referenced tune; retain the row's position, note and call. */
export function replaceEntryRef(
  doc: PlaylistDoc,
  entryId: string,
  ref: PlaylistTuneRef,
  now: string = nowIso(),
): PlaylistDoc {
  const at = doc.entries.findIndex((e) => e.entryId === entryId);
  if (at < 0 || doc.entries[at].ref === ref) return doc;
  const entries = [...doc.entries];
  entries[at] = { ...entries[at], ref };
  return touch(doc, { entries }, now);
}

/** Save or clear one row's call. */
export function setEntryCall(
  doc: PlaylistDoc,
  entryId: string,
  call: PlaylistCall | null,
  now: string = nowIso(),
): PlaylistDoc {
  let changed = false;
  const entries = doc.entries.map((e) => {
    if (e.entryId !== entryId) return e;
    if (!call && !e.call) return e;
    changed = true;
    const { call: _dropped, ...rest } = e;
    return call ? { ...rest, call } : rest;
  });
  return changed ? touch(doc, { entries }, now) : doc;
}

/**
 * Copy a playlist.
 *
 * The copy takes a NEW identity and fresh entry ids, and records `derivedFrom`
 * rather than referencing the original's rows. A duplicate that shared entry
 * ids would look to a later merge like the same rows in two places, and
 * editing one would appear to edit the other.
 */
export function duplicatePlaylistDoc(
  doc: PlaylistDoc,
  name?: string,
  now: string = nowIso(),
): PlaylistDoc {
  const copyName = (name ?? `${doc.name} copy`).trim() || 'Untitled';
  return {
    ...doc,
    id: makePlaylistId(copyName),
    name: copyName,
    entries: doc.entries.map((e) => ({ ...e, entryId: makeEntryId() })),
    derivedFrom: { id: doc.id, ...(doc.revisionId ? { revisionId: doc.revisionId } : {}) },
    revisionId: undefined,
    createdAt: now,
    updatedAt: now,
  };
}

// --- working through a set --------------------------------------------------

export function entryIndex(doc: PlaylistDoc, entryId: string): number {
  return doc.entries.findIndex((e) => e.entryId === entryId);
}

/**
 * The row `delta` places from `entryId`, or null at the ends.
 *
 * Deliberately does NOT wrap. A set has a last tune, and a "next" that silently
 * restarts the gig would be worse than a disabled button — the player would
 * only notice when the wrong tune came up.
 */
export function neighbourEntry(
  doc: PlaylistDoc,
  entryId: string,
  delta: number,
): PlaylistEntry | null {
  const at = entryIndex(doc, entryId);
  if (at < 0) return null;
  return doc.entries[at + delta] ?? null;
}

/** A resolved set cursor: where the player is, and what is either side. */
export interface SetPosition {
  list: PlaylistDoc;
  /** The row the cursor names, or null if it was removed while parked on it. */
  entry: PlaylistEntry | null;
  /** 0-based, or -1 when `entry` is null. */
  index: number;
  total: number;
  prev: PlaylistEntry | null;
  next: PlaylistEntry | null;
}

/**
 * Turn a stored cursor into something the UI can render.
 *
 * Two ways a cursor goes stale, handled differently on purpose. A deleted
 * PLAYLIST resolves to null — there is no set to be in, so the strip goes away.
 * A deleted ROW keeps the set: the player is still working through it, they
 * have just lost their place, so `next` points at the top of the list and the
 * UI can offer a way back in rather than dropping them out of a gig silently.
 */
export function resolveSetCursor(
  playlists: readonly PlaylistDoc[],
  cursor: { playlistId: string; entryId: string } | null,
): SetPosition | null {
  if (!cursor) return null;
  const list = playlists.find((p) => p.id === cursor.playlistId);
  if (!list) return null;
  const total = list.entries.length;
  const index = entryIndex(list, cursor.entryId);
  if (index < 0) {
    return { list, entry: null, index: -1, total, prev: null, next: list.entries[0] ?? null };
  }
  return {
    list,
    entry: list.entries[index],
    index,
    total,
    prev: list.entries[index - 1] ?? null,
    next: list.entries[index + 1] ?? null,
  };
}

// --- tombstones -------------------------------------------------------------

export function recordTombstone(
  tombstones: PlaylistTombstone[],
  id: string,
  now: string = nowIso(),
): PlaylistTombstone[] {
  const cutoff = Date.parse(now) - TOMBSTONE_TTL_MS;
  return [
    { id, deletedAt: now },
    ...tombstones.filter((t) => t.id !== id && Date.parse(t.deletedAt) > cutoff),
  ];
}
